/**
 * Toast Notification Hook
 *
 * Provides a simple toast notification system for displaying
 * success, error, warning, and info messages.
 *
 * Backed by a module-level store (not per-instance React state) so that
 * ANY component — including ones rendered deep in the tree like
 * WatchZoneManager, far from the single <ToastContainer> in App — can raise
 * a toast without prop-drilling. Every `useToast()` consumer subscribes to
 * the same list, so a toast shown anywhere renders in App's container.
 * A standalone `toast` object is also exported for non-component code.
 */

import { useState, useEffect, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number; // ms, 0 = no auto-dismiss
}

interface UseToastReturn {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => string;
  dismissToast: (id: string) => void;
  dismissAllToasts: () => void;
}

let toastIdCounter = 0;
let store: Toast[] = [];
const listeners = new Set<(toasts: Toast[]) => void>();
const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  // Hand every subscriber the same immutable snapshot.
  for (const listener of listeners) listener(store);
}

function showToast(toast: Omit<Toast, 'id'>): string {
  const id = `toast-${++toastIdCounter}`;
  const duration = toast.duration ?? 5000; // Default 5 seconds
  const newToast: Toast = { ...toast, id, duration };
  store = [...store, newToast];
  emit();

  // Auto-dismiss if duration > 0
  if (duration > 0) {
    const timer = setTimeout(() => dismissToast(id), duration);
    dismissTimers.set(id, timer);
  }

  return id;
}

function dismissToast(id: string) {
  const timer = dismissTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(id);
  }
  store = store.filter((t) => t.id !== id);
  emit();
}

function dismissAllToasts() {
  for (const timer of dismissTimers.values()) clearTimeout(timer);
  dismissTimers.clear();
  store = [];
  emit();
}

/**
 * Standalone toast API for code that isn't a React component
 * (utilities, event handlers, service wrappers).
 */
export const toast = {
  show: showToast,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
};

export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<Toast[]>(store);

  useEffect(() => {
    listeners.add(setToasts);
    // Sync immediately in case the store changed between render and effect.
    setToasts(store);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  const stableShow = useCallback(showToast, []);
  const stableDismiss = useCallback(dismissToast, []);
  const stableDismissAll = useCallback(dismissAllToasts, []);

  return {
    toasts,
    showToast: stableShow,
    dismissToast: stableDismiss,
    dismissAllToasts: stableDismissAll,
  };
}
